import { createContext, useContext, useEffect, useState } from "react";
import $ from "jquery";
import jwt_decode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { ENDPOINT_ROUTES, BASE_URL } from "./routes";
import { api } from "./api_instructions";
import protocolsApi from "./api_protocols";
import lastPagesApi from "./api_last_pages";
import invoicesApi from "./api_invoices";
import worktimeApi from "./api_worktime";
import settingsApi from "./api_settings";
import offerTemplatesApi from "./api_offer_templates";
import offerPricesApi from "./api_offer_prices";

const authContext = createContext();

function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

function getToken() {
  let name = "token=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return undefined;
}

function isTokenValid(token) {
  try {
    const decodedToken = jwt_decode(token);
    const tokenExpiration = decodedToken?.exp;

    if (typeof tokenExpiration !== "number") return false;

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return tokenExpiration > nowInSeconds;
  } catch (error) {
    return false;
  }
}

function getUser() {
  const token = getToken();

  if (token && isTokenValid(token)) {
    $.ajaxSetup({
      headers: { "x-auth-token": token },
      timeout: 10000,
    });
    return createUser(token);
  }

  if (token) {
    setCookie("token", null, 0);
  }

  return null;
}

function createUser(token) {
  if (!isTokenValid(token)) return null;

  let jwtUser = jwt_decode(token);
  return {
    ...jwtUser,
    isAdmin: jwtUser.role === "ADMIN",
    getPrivilege: (endpoint) => {
      const privilege = { view: false, edit: false };

      if (jwtUser.role === "ADMIN") return { view: true, edit: true };

      const { privilages } = jwtUser;

      if (privilages === undefined || privilages === null) return privilege;

      if (privilages[endpoint] !== undefined) {
        privilege.view = privilages[endpoint]["view"];
        privilege.edit = privilages[endpoint]["edit"];
      }

      return privilege;
    },
  };
}

export function useAuth() {
  return useContext(authContext);
}

export default function ProvideAuth({ children }) {
  const auth = useProvideAuth();

  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export function PrivateRoute({ children, ...rest }) {
  let navigate = useNavigate();
  let auth = useAuth();


  useEffect(() => {
    if (!auth.user) return navigate("/login", { replace: true });
  });

  return children;
}

function useProvideAuth() {
  const [user, setUser] = useState(getUser());
  const [workTimeRecord, setWorkTimeRecord] = useState(null);

  useEffect(() => {
    if (!user) {
      setWorkTimeRecord(null);
      return;
    }

    refreshCurrentWorkTime();
  }, [user]);

  const setCurrentWorkTime = (record) => {
    setWorkTimeRecord(record || null);
  };

  const clearCurrentWorkTime = () => {
    setWorkTimeRecord(null);
  };

  const refreshCurrentWorkTime = async () => {
    if (!user) {
      setWorkTimeRecord(null);
      return { success: false };
    }

    const result = await worktimeApi.api.getCurrentWorkTime();

    

    if (result?.success) {
      console.log("Current work time record refreshed:", result.content);
      setWorkTimeRecord(result.content || null);
    }

    return result;
  };

  const baseUrl = BASE_URL;

  const signin = (login, password, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.auth;

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: {
        login: login,
        password: password,
      },
      success: function (result, status, xhr) {
        if (xhr.status === 200) {
          setCookie("token", result, 30);
          setUser(createUser(result));
          callback({ success: true, status: xhr.status });
        } else if (xhr.status === 201) {
          callback({
            success: false,
            verifySms: true,
            expiresIn: result.expiresIn,
            status: xhr.status,
          });
        } else {
          callback({
            success: false,
            error: "Nieoczekiwana odpowiedź serwera.",
            status: xhr.status,
          });
        }
      },
      error: function (xhr, status, error) {
        callback({
          success: false,
          status: xhr.status,
          content: xhr.responseJSON,
        });
      },
    });
  };

  const verifySms = (login, code, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.auth + "/sms";

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: {
        login: login,
        code: code,
      },
      success: function (result, status, xhr) {
        setCookie("token", result, 30);
        setUser(createUser(result));
        callback({ success: true });
      },
      error: function (xhr, status, error) {
        callback({ success: false });
      },
    });
  };

  const addEditClient = (formData, callback, edition) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.clients;

    $.ajax({
      url: authEndpointAddress,
      type: edition ? "PUT" : "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getClients = (callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.clients;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getShortClients = (callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.clients + "/shortinfo";

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getClient = (client_id, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.clients + "/" + client_id;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteClient = (clientId, reason, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.clients;

    $.ajax({
      url: endpoint,
      type: "DELETE",
      data: JSON.stringify({ id: clientId, reason: reason }),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getBuildings = (callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.buildings;

    $.ajax({
      url: endpoint,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getImageFolders = (buildingId, callback) => {
    const endpoint =
      BASE_URL +
      ENDPOINT_ROUTES.buildings +
      "/buildingImageFolders/" +
      buildingId;

    $.ajax({
      url: endpoint,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getBuildingImagesList = (buildingId, callback) => {
    const endpoint =
      BASE_URL +
      ENDPOINT_ROUTES.buildings +
      "/buildingattachments/" +
      buildingId;

    $.ajax({
      url: endpoint,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getBuildingProtocolFiles = (buildingId, callback) => {
    const endpoint =
      BASE_URL +
      ENDPOINT_ROUTES.buildings +
      "/protocolattachments/" +
      buildingId;

    $.ajax({
      url: endpoint,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const uploadBuildingImage = async (formData) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.buildings + "/buildingattachments";

    try {
      const result = await $.ajax({
        url: endpoint,
        type: "POST",
        data: formData,
        processData: false, // Necessary to send FormData as is
        contentType: false, // Necessary to prevent jQuery from overriding the content type
      });

      return { success: true, content: result };
    } catch (xhr) {
      return { success: false, error: xhr.responseText || xhr.statusText };
    }
  };

  const getEventImages = (eventId, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.buildings + "/buildingImages/" + eventId;

    $.ajax({
      url: endpoint,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const downloadEventImage = (imageId, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.buildings + "/images/" + imageId;

    $.ajax({
      url: endpoint,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({ success: true, content: blob });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const downloadNoteImage = (eventId, imageId, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/notes/" + eventId + "/" + imageId;

    $.ajax({
      url: endpoint,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({ success: true, content: blob });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getShortBuildings = (callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.buildings + "/shortinfo";

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        let errorMessage;

        // Detect offline or unreachable server
        if (xhr.status === 0) {
          if (!navigator.onLine) {
            errorMessage = "Brak połączenia z Internetem.";
          } else {
            errorMessage = "Nie można połączyć się z serwerem.";
          }
        } else if (xhr.responseText) {
          errorMessage = xhr.responseText;
        } else {
          errorMessage = "Wystąpił nieznany błąd.";
        }
        callback({ success: false, error: errorMessage });
      },
    });
  };

  const addEditBuilding = (formData, edition = false, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.buildings;

    $.ajax({
      url: endpoint,
      type: edition ? "PUT" : "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteBuilding = (buildingId, reason, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.buildings;

    $.ajax({
      url: endpoint,
      type: "DELETE",
      data: JSON.stringify({ id: buildingId, reason: reason }),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteBuildingImage = (imageId, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.buildings + "/buildingattachments/" + imageId;

    $.ajax({
      url: endpoint,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const addNewRajCompany = (formData, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.rajcompanies;

    $.ajax({
      url: endpoint,
      type: "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      // dataType: 'json',
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const editRajCompany = (formData, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.rajcompanies;

    $.ajax({
      url: endpoint,
      type: "PUT",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteRajCompany = (id, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id;

    $.ajax({
      url: endpoint,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getRajCompanies = (context, callback) => {
    const contextUrl = context !== null ? "/" + context : "";
    const authEndpointAddress =
      BASE_URL + contextUrl + ENDPOINT_ROUTES.rajcompanies;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getRajCompanyLogo = (id, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/logo";

    $.ajax({
      url: endpoint,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({
          success: true,
          content: { file: blob, type: xhr.getResponseHeader("Content-Type") },
        });
      },
      error: function (xhr, status, error) {
        let errorMessage;

        if (xhr.status === 0) {
          errorMessage = !navigator.onLine
            ? "Brak połączenia z Internetem."
            : "Nie można połączyć się z serwerem.";
        } else if (xhr.status === 404) {
          errorMessage = "Nie znaleziono logo.";
        } else {
          errorMessage = "Nie udało się pobrać logo.";
        }

        callback({ success: false, error: errorMessage });
      },
    });
  };

  const uploadRajCompanyLogo = (id, file, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/logo";
    const data = new FormData();
    data.append("file", file);

    $.ajax({
      url: endpoint,
      type: "POST",
      data: data,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteRajCompanyLogo = (id, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/logo";

    $.ajax({
      url: endpoint,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getRajCompanyStamp = (id, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/stamp";

    $.ajax({
      url: endpoint,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({
          success: true,
          content: { file: blob, type: xhr.getResponseHeader("Content-Type") },
        });
      },
      error: function (xhr, status, error) {
        let errorMessage;

        if (xhr.status === 0) {
          errorMessage = !navigator.onLine
            ? "Brak połączenia z Internetem."
            : "Nie można połączyć się z serwerem.";
        } else if (xhr.status === 404) {
          errorMessage = "Nie znaleziono pieczątki.";
        } else {
          errorMessage = "Nie udało się pobrać pieczątki.";
        }

        callback({ success: false, error: errorMessage });
      },
    });
  };

  const uploadRajCompanyStamp = (id, file, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/stamp";
    const data = new FormData();
    data.append("file", file);

    $.ajax({
      url: endpoint,
      type: "POST",
      data: data,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteRajCompanyStamp = (id, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/stamp";

    $.ajax({
      url: endpoint,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getRajCompanyFireCertificate = (id, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/fire-certificate";

    $.ajax({
      url: endpoint,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({
          success: true,
          content: { file: blob, type: xhr.getResponseHeader("Content-Type") },
        });
      },
      error: function (xhr, status, error) {
        let errorMessage;

        if (xhr.status === 0) {
          errorMessage = !navigator.onLine
            ? "Brak połączenia z Internetem."
            : "Nie można połączyć się z serwerem.";
        } else if (xhr.status === 404) {
          errorMessage = "Nie znaleziono świadectwa przeciwpożarowego.";
        } else {
          errorMessage = "Nie udało się pobrać świadectwa przeciwpożarowego.";
        }

        callback({ success: false, error: errorMessage });
      },
    });
  };

  const uploadRajCompanyFireCertificate = (id, file, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/fire-certificate";
    const data = new FormData();
    data.append("file", file);

    $.ajax({
      url: endpoint,
      type: "POST",
      data: data,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteRajCompanyFireCertificate = (id, callback) => {
    const endpoint =
      BASE_URL + ENDPOINT_ROUTES.rajcompanies + "/" + id + "/fire-certificate";

    $.ajax({
      url: endpoint,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getUsers = (callback, context) => {
    const contextUrl = context !== undefined ? "/" + context : "";
    const authEndpointAddress = BASE_URL + contextUrl + ENDPOINT_ROUTES.users;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        let errorMessage;

        // Detect offline or unreachable server
        if (xhr.status === 0) {
          if (!navigator.onLine) {
            errorMessage = "Brak połączenia z Internetem.";
          } else {
            errorMessage = "Nie można połączyć się z serwerem.";
          }
        } else if (xhr.responseText) {
          errorMessage = xhr.responseText;
        } else {
          errorMessage = "Wystąpił nieznany błąd.";
        }
        callback({ success: false, error: errorMessage });
      },
    });
  };

  const getUserDepartments = (callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.users + "/departments";

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getPrevilagesList = (worker, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.users + "/privilages/" + worker.id;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const grantPrivilages = (formData, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.users + "/privilages";
    $.ajax({
      url: authEndpointAddress,
      type: "PUT",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        console.log(xhr);
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const addTraining = (formData, progressHandler, callback) => {
    $.ajax({
      url: BASE_URL + ENDPOINT_ROUTES.users + "/training",
      type: "POST",
      data: formData,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      xhr: function () {
        let myXhr = $.ajaxSettings.xhr();
        if (myXhr.upload) {
          myXhr.upload.addEventListener("progress", progressHandler, false);
        }
        return myXhr;
      },
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const downloadTraining = (trainingId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.users + "/training/" + trainingId;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({
          success: true,
          content: { file: blob, type: xhr.getResponseHeader("Content-Type") },
        });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getSignature = (userId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.users + "/signature/" + userId;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({ success: true, content: blob });
      },
      error: function (xhr, status, error) {
        let errorMessage;
        // Detect offline or unreachable server
        if (xhr.status === 0) {
          if (!navigator.onLine) {
            errorMessage = "Brak połączenia z Internetem.";
          } else {
            errorMessage =
              "Nie można połączyć się z serwerem. Podgląd nie jest dostepny w trybie offline.";
          }
        } else if (xhr.status == 404) {
          errorMessage = "Nie znaleziono podpisu.";
        } else if (xhr.responseText) {
          errorMessage = xhr.responseText;
        } else {
          errorMessage = "Wystąpił nieznany błąd.";
        }
        callback({ success: false, error: errorMessage });
      },
    });
  };

  const addUser = (formData, edition, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.users;

    $.ajax({
      url: authEndpointAddress,
      type: edition ? "PUT" : "POST",
      data: formData,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        console.log(xhr);
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const addEditDevice = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.devices;

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: formData,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const uploadAssortments = (formData, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.assortments + "/import";

    $.ajax({
      url: authEndpointAddress,
      type: "PUT",
      data: formData,
      processData: false, // tell jQuery not to process the data
      contentType: false,
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const removeDevice = (deviceId, callback) => {
    const endpoint = BASE_URL + ENDPOINT_ROUTES.devices + "/" + deviceId;

    $.ajax({
      url: endpoint,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteUser = (userId, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.users + "/" + userId;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteTraining = (trainingId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.users + "/training/" + trainingId;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const createTeam = (callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.teams;

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  /* add user to team */
  const putUser = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.teams;

    $.ajax({
      url: authEndpointAddress,
      type: "PUT",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const removeUser = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.teams + "/user";

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteTeam = (teamId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.teams + "/team/" + teamId;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getTeams = (callback, context) => {
    const contextUrl = context !== undefined ? "/" + context : "";
    const authEndpointAddress = BASE_URL + contextUrl + ENDPOINT_ROUTES.teams;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const addOffer = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.offers;

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getOffers = (callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.offers;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getOffer = (offerId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.offers + "/offer/" + offerId;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getTemplate = (offerType, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.offers + "/template/" + offerType;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const removeOffer = (offerId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.offers + "/" + offerId;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getBuildingOffers = (buildingId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.offers + "/" + buildingId;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  // const joinOfferWithEvent = (offerId, eventId, callback)=>{
  //     const authEndpointAddress =  BASE_URL + ENDPOINT_ROUTES.offers +'/joinOfferWithEvent';
  //
  //     $.ajax({
  //         url:authEndpointAddress,
  //         type: 'PUT',
  //         data: JSON.stringify({offerId:offerId, eventId: eventId}),
  //         contentType: 'application/json; charset=utf-8',
  //         success : function(result,status,xhr){
  //             callback({success:true, status:status, content:result})
  //         },
  //         error : function(xhr,status,error){
  //             callback({success:false, error:xhr.responseText});
  //         }
  //     });
  // }

  const downloadOfferBlob = (id, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.offers + "/download/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({ success: true, content: blob });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getNotes = (id, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.notes + "/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const addNote = (formData, edition, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.notes;

    $.ajax({
      url: authEndpointAddress,
      type: edition ? "PUT" : "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getEventNotes = (id, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/notes/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const saveEventNote = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.calendar + "/notes";

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: formData,
      processData: false, // Don't process the data
      contentType: false, // Let the browser set the correct content type
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };
  const removeEventNote = (id, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/notes/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (response, status, xhr) {
        callback({ success: true, content: response });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const sendTeamColorValue = (hexColor, teamId, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.teams + "/color";

    $.ajax({
      url: authEndpointAddress,
      type: "PUT",
      data: JSON.stringify({ color: hexColor, id: teamId }),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const viewPdf = (blob) => {
    let newBlob = new Blob([blob], { type: "application/pdf" });

    let objectURL = URL.createObjectURL(newBlob);
    window.open(objectURL, "_blank");
  };

  const viewFile = (blob, type) => {
    let newBlob = new Blob([blob], { type: type });

    let objectURL = URL.createObjectURL(newBlob);
    window.open(objectURL, "_blank");
  };

  const getUrl = (blob) => {
    let newBlob = new Blob([blob], { type: "application/pdf" });

    return URL.createObjectURL(newBlob);
  };

  const saveFile = (name, type, data) => {
    if (data !== null && navigator.msSaveBlob)
      return navigator.msSaveBlob(new Blob([data], { type: type }), name);

    const a = $("<a style='display: none;'/>");
    const url = window.URL.createObjectURL(new Blob([data], { type: type }));
    a.attr("href", url);
    a.attr("download", name);
    a.attr("target", "_blank");
    $("body").append(a);
    a[0].click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const signout = (cb) => {
    setCookie("token", null, 0);
    setUser(null);
  };

  const addEvent = (formData, edition, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.calendar;
    const isMultipart = formData instanceof FormData;

    $.ajax({
      url: authEndpointAddress,
      type: edition ? "PUT" : "POST",
      data: isMultipart ? formData : JSON.stringify(formData),
      processData: isMultipart ? false : undefined,
      contentType: isMultipart ? false : "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const addCustomEvent = (formData, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/customEvent";
    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getBuildingEvents = (buildingId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/search/" + buildingId;
    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const loadEvents = (startDate, endDate, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/" + startDate + "/" + endDate;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const loadEventsToDateWithStatus = ({
    dateTo,
    status,
    page = 1,
    limit = 20,
    q = "",
    sortBy = "date", // "date" | "building"
    sortDir = "asc", // "asc" | "desc"
  }) => {
    const params = new URLSearchParams();
    params.set("status", status ?? "");
    params.set("page", String(page));
    params.set("limit", String(limit));

    if (q && String(q).trim().length > 0) params.set("q", String(q).trim());
    if (sortBy) params.set("sortBy", String(sortBy));
    if (sortDir) params.set("sortDir", String(sortDir));

    const endpoint =
      `${BASE_URL}${ENDPOINT_ROUTES.calendar}/selectWithStatusAndToDate/${dateTo}` +
      `?${params.toString()}`;

    return new Promise((resolve) => {
      $.ajax({
        url: endpoint,
        type: "GET",
        success: function (result, statusText, xhr) {
          resolve({
            success: true,
            status: xhr?.status,
            content: result,
            page,
            limit,
            q: String(q ?? "").trim(),
            sortBy,
            sortDir,
          });
        },
        error: function (xhr, statusText, error) {
          resolve({
            success: false,
            status: xhr?.status,
            error: xhr?.responseText || error,
            page,
            limit,
            q: String(q ?? "").trim(),
            sortBy,
            sortDir,
          });
        },
      });
    });
  };

  const loadEventsHistory = (callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/history";

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const loadEventStates = (eventId, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + "/states/" + eventId;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const sendEventState = (eventId, state, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.calendar + "/states";

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: JSON.stringify({
        state: state,
        eventId: eventId,
      }),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const loadTodos = (callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.todos;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const createTodo = (title, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.todos;

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: JSON.stringify({
        title: title,
      }),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const saveTodoChanges = (data, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.todos;

    $.ajax({
      url: authEndpointAddress,
      type: "PUT",
      data: JSON.stringify(data),
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const sendRealization = (todoId, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.todos + "/" + todoId;

    $.ajax({
      url: authEndpointAddress,
      type: "PUT",
      contentType: "application/json; charset=utf-8",
      success: function (result, status, xhr) {
        callback({ success: true, status: status, content: result });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getDevices = (callback) => {
    let authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.devices;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      contentType: "application/json; charset=utf-8",
      success: function (response, status, xhr) {
        callback({ success: true, content: response });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const downloadDeviceFile = (id, callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.devices + "/download/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({
          success: true,
          content: { file: blob, type: xhr.getResponseHeader("Content-Type") },
        });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const displayOffer = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.offers + "/display";

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      contentType: "application/json; charset=utf-8",
      data: JSON.stringify(formData),
      xhrFields: {
        responseType: "blob",
      },
      success: function (blob, status, xhr) {
        callback({ success: true, content: blob });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: error });
      },
    });
  };

  const deleteNote = (id, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.notes + "/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (response, status, xhr) {
        callback({ success: true, content: response });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteEvent = (id, callback, isCustomEvent = false) => {
    const prefix = isCustomEvent ? "/customEvent" : "";
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.calendar + prefix + "/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      contentType: "application/json; charset=utf-8",
      success: function (response, status, xhr) {
        callback({ success: true, content: response });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const deleteTodo = (id, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.todos + "/" + id;

    $.ajax({
      url: authEndpointAddress,
      type: "DELETE",
      success: function (response, status, xhr) {
        callback({ success: true, content: response });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const getAssortments = (callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.assortments;
    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      contentType: "application/json; charset=utf-8",
      success: function (blob, status, xhr) {
        callback({ success: true, content: blob });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const exportAssortments = (callback) => {
    const authEndpointAddress =
      BASE_URL + ENDPOINT_ROUTES.assortments + "/export";
    $.ajax({
      url: authEndpointAddress,
      type: "GET",
      xhrFields: {
        responseType: "blob",
      },
      contentType: "application/json; charset=utf-8",
      success: function (blob, status, xhr) {
        callback({
          success: true,
          content: { file: blob, type: xhr.getResponseHeader("Content-Type") },
        });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  const saveAssortments = (formData, callback) => {
    const authEndpointAddress = BASE_URL + ENDPOINT_ROUTES.assortments;

    $.ajax({
      url: authEndpointAddress,
      type: "POST",
      data: JSON.stringify(formData),
      contentType: "application/json; charset=utf-8",
      success: function (response, status, xhr) {
        callback({ success: true, content: response });
      },
      error: function (xhr, status, error) {
        callback({ success: false, error: xhr.responseText });
      },
    });
  };

  return {
    user,
    workTimeRecord,
    setWorkTimeRecord: setCurrentWorkTime,
    clearWorkTimeRecord: clearCurrentWorkTime,
    baseUrl,
    signin,
    signout,
    verifySms,
    buildings: {
      get: getBuildings,
      getShort: getShortBuildings,
      add: addEditBuilding,
      getEventImages,
      getImageFolders,
      getBuildingImagesList,
      uploadBuildingImage,
      deleteBuildingImage,
      downloadEventImage,
      deleteBuilding,
      getBuildingProtocolFiles,
    },

    clients: {
      add: addEditClient,
      get: getClients,
      getShort: getShortClients,
      getClient: getClient,
      deleteClient,
    },
    users: {
      get: getUsers,
      add: addUser,
      remove: deleteUser,
      getDepartments: getUserDepartments,
      getPrevilages: getPrevilagesList,
      grantPrivilages: grantPrivilages,
      addTraining: addTraining,
      downloadTraining: downloadTraining,
      deleteTraining,
      getSignature,
    },
    teams: {
      create: createTeam,
      get: getTeams,
      put: putUser,
      remove: removeUser,
      setTeamColor: sendTeamColorValue,
      deleteTeam,
    },
    rajcompanies: {
      add: addNewRajCompany,
      edit: editRajCompany,
      get: getRajCompanies,
      remove: deleteRajCompany,
      getLogo: getRajCompanyLogo,
      uploadLogo: uploadRajCompanyLogo,
      removeLogo: deleteRajCompanyLogo,
      getStamp: getRajCompanyStamp,
      uploadStamp: uploadRajCompanyStamp,
      removeStamp: deleteRajCompanyStamp,
      getFireCertificate: getRajCompanyFireCertificate,
      uploadFireCertificate: uploadRajCompanyFireCertificate,
      removeFireCertificate: deleteRajCompanyFireCertificate,
    },
    offers: {
      add: addOffer,
      get: getOffers,
      getOffer,
      remove: removeOffer,
      getBuildingOffers,
      download: downloadOfferBlob,
      getTemplate,
      displayOffer,
      templates: { ...offerTemplatesApi.api },
    },
    notes: { add: addNote, get: getNotes, remove: deleteNote },
    calendar: {
      add: addEvent,
      addCustomEvent: addCustomEvent,
      getBuildingEvents,
      load: loadEvents,
      remove: deleteEvent,
      getHistory: loadEventsHistory,
      getEventStates: loadEventStates,
      sendEventState: sendEventState,
      getEventNotes,
      saveEventNote,
      removeEventNote,
      downloadNoteImage,
      loadEventsToDateWithStatus,
    },
    todo: {
      get: loadTodos,
      create: createTodo,
      save: saveTodoChanges,
      realize: sendRealization,
      remove: deleteTodo,
    },
    protocols: { ...protocolsApi.api },
    lastPages: { ...lastPagesApi.api },
    invoices: { ...invoicesApi.api },
    workTime: { ...worktimeApi.api },
    settings: { ...settingsApi.api },
    offerPrices: { ...offerPricesApi.api },
    devices: {
      get: getDevices,
      add: addEditDevice,
      remove: removeDevice,
      downloadDeviceFile,
    },
    assortments: {
      get: getAssortments,
      save: saveAssortments,
      exportAssortments,
      uploadAssortments,
    },
    instructions: { ...api },

    viewPdf,
    saveFile,
    getUrl,
    viewFile,
  };
}
